import {
  ANALYTICS_EVENTS,
  ANALYTICS_PROPERTY_KEYS,
  COMPONENT_OUTCOMES,
  DCM_PAGES,
  ERROR_TYPES,
  FORM_INTERACTION_TYPES,
} from '../constants';
import { trackAnalyticsEvent } from '../core/analyticsClient';
import { flattenFormikValidationErrors } from '../utils/flattenFormikValidationErrors';

type CampaignBuilderEventContext = {
  campaignId?: string;
  lineItemId?: string;
  adAccountId?: string;
};

const buildCampaignBuilderProps = ({
  campaignId,
  lineItemId,
  adAccountId,
}: CampaignBuilderEventContext): Record<string, unknown> => ({
  [ANALYTICS_PROPERTY_KEYS.CAMPAIGN_ID]: campaignId,
  [ANALYTICS_PROPERTY_KEYS.LINE_ITEM_ID]: lineItemId,
  [ANALYTICS_PROPERTY_KEYS.AD_ACCOUNT_ID]: adAccountId,
  [ANALYTICS_PROPERTY_KEYS.PAGE]: DCM_PAGES.CAMPAIGN_BUILDER,
  [ANALYTICS_PROPERTY_KEYS.COMPONENT_OUTCOME]: COMPONENT_OUTCOMES.USER,
});

export type TrackValidationErrorsShownParams = CampaignBuilderEventContext & {
  errors: Record<string, unknown>;
};

export const trackValidationErrorsShown = ({
  campaignId,
  lineItemId,
  adAccountId,
  errors,
}: TrackValidationErrorsShownParams): void => {
  flattenFormikValidationErrors(errors).forEach(({ fieldName, stepName, errorCode, errorType }) => {
    trackAnalyticsEvent(ANALYTICS_EVENTS.VALIDATION_ERROR_SHOWN, {
      ...buildCampaignBuilderProps({ campaignId, lineItemId, adAccountId }),
      [ANALYTICS_PROPERTY_KEYS.FIELD_NAME]: fieldName,
      [ANALYTICS_PROPERTY_KEYS.STEP_NAME]: stepName,
      [ANALYTICS_PROPERTY_KEYS.ERROR_CODE]: errorCode,
      [ANALYTICS_PROPERTY_KEYS.ERROR_TYPE]: errorType,
    });
  });
};

export type TrackFormFieldInteractionParams = CampaignBuilderEventContext & {
  fieldName: string;
  stepName: string;
  interactionType: (typeof FORM_INTERACTION_TYPES)[keyof typeof FORM_INTERACTION_TYPES];
};

export const trackFormFieldInteraction = ({
  campaignId,
  lineItemId,
  adAccountId,
  fieldName,
  stepName,
  interactionType,
}: TrackFormFieldInteractionParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.FORM_FIELD_INTERACTION, {
    ...buildCampaignBuilderProps({ campaignId, lineItemId, adAccountId }),
    [ANALYTICS_PROPERTY_KEYS.FIELD_NAME]: fieldName,
    [ANALYTICS_PROPERTY_KEYS.STEP_NAME]: stepName,
    [ANALYTICS_PROPERTY_KEYS.INTERACTION_TYPE]: interactionType,
  });
};

export type TrackTooltipViewedParams = CampaignBuilderEventContext & {
  tooltipId: string;
  stepName: string;
};

export const trackTooltipViewed = ({
  campaignId,
  lineItemId,
  adAccountId,
  tooltipId,
  stepName,
}: TrackTooltipViewedParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.TOOLTIP_VIEWED, {
    ...buildCampaignBuilderProps({ campaignId, lineItemId, adAccountId }),
    [ANALYTICS_PROPERTY_KEYS.TOOLTIP_ID]: tooltipId,
    [ANALYTICS_PROPERTY_KEYS.STEP_NAME]: stepName,
    [ANALYTICS_PROPERTY_KEYS.FIELD_NAME]: tooltipId,
  });
};

export type TrackCampaignFormElementClickedParams = CampaignBuilderEventContext & {
  elementSelector: string;
  elementLabel: string;
  stepName: string;
};

export const trackCampaignFormElementClicked = ({
  campaignId,
  lineItemId,
  adAccountId,
  elementSelector,
  elementLabel,
  stepName,
}: TrackCampaignFormElementClickedParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.CAMPAIGN_FORM_ELEMENT_CLICKED, {
    ...buildCampaignBuilderProps({ campaignId, lineItemId, adAccountId }),
    [ANALYTICS_PROPERTY_KEYS.ELEMENT_SELECTOR]: elementSelector,
    [ANALYTICS_PROPERTY_KEYS.ELEMENT_LABEL]: elementLabel,
    [ANALYTICS_PROPERTY_KEYS.STEP_NAME]: stepName,
  });
};

export type TrackLowInventoryWarningParams = CampaignBuilderEventContext & {
  inventoryPercentage?: number;
  revisionNumber?: string;
  isValidatedEdit?: boolean;
};

export const trackLowInventoryWarning = ({
  campaignId,
  lineItemId,
  adAccountId,
  inventoryPercentage,
  revisionNumber,
  isValidatedEdit,
}: TrackLowInventoryWarningParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.LOW_INVENTORY_WARNING, {
    ...buildCampaignBuilderProps({ campaignId, lineItemId, adAccountId }),
    [ANALYTICS_PROPERTY_KEYS.INVENTORY_PERCENTAGE]: inventoryPercentage,
    [ANALYTICS_PROPERTY_KEYS.REVISION_NUMBER]: revisionNumber,
    [ANALYTICS_PROPERTY_KEYS.IS_VALIDATED_EDIT]: isValidatedEdit,
    [ANALYTICS_PROPERTY_KEYS.ERROR_TYPE]: ERROR_TYPES.USER,
  });
};
